import React from 'react';
import { useTranslation } from 'react-i18next';
import { GovSkeleton } from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import type { SubmissionConnection } from '@gov-nx/core/hooks';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type { DataMessage } from '@gov-nx/module/page';
import { PageCode } from '@gov-nx/module/page';
import type { FormButtonProps } from '@gov-nx/ui/web';
import {
	Button,
	commonSelectPropsToSelectProps,
	Dialog,
	FormSelect,
	LayoutGap,
} from '@gov-nx/ui/web';

const Skeleton = () => {
	const { t } = useTranslation(LocalizeNameSpaceTypes.Global);

	return (
		<LayoutGap>
			<div className="flex flex-col mt-6 [ md:flex-row ]">
				<div className="w-2/5">
					<GovSkeleton
						height="22px"
						color="neutral"
						aria-label={t('loading.wcag.processing')}
						className="[ md:ml-3 ]"
					/>
				</div>
			</div>
		</LayoutGap>
	);
};

type ConnectToAnotherSubmissionDialogProps = Omit<
	SubmissionConnection['connect'],
	'connect'
>;

export const ConnectToAnotherSubmissionDialog = ({
	form,
	fields,
	isConnectModalOpen,
	isLoading,
	isConnecting,
	onConnectSubmit,
	onConnectCancel,
}: ConnectToAnotherSubmissionDialogProps) => {
	const { t } = useLocale(PageCode['moje-podani']);
	const { t: td } = useLocale(PageCode['datove-schranky']);
	return (
		<GovFormWeb
			formDefinition={form}
			onSubmit={onConnectSubmit}>
			<Dialog
				isOpen={isConnectModalOpen}
				onClose={onConnectCancel}
				title={<h3>{td('formular.pripojit-k-moje-podani.titulek')}</h3>}
				footer={
					<Button
						isLoading={isConnecting}
						nativeType="submit"
						aria-label={t('akce.pripojit-k-jinemu-podani-ulozit')}
						isDisabled={isConnecting}
						color="primary"
						type="solid"
						size="m">
						{t('akce.pripojit-k-jinemu-podani-ulozit')}
					</Button>
				}>
				{isLoading ? (
					<Skeleton />
				) : (
					<LayoutGap gap="m">
						<FormSelect
							{...commonSelectPropsToSelectProps(fields.submissionId)}
						/>
					</LayoutGap>
				)}
			</Dialog>
		</GovFormWeb>
	);
};

export const useConnectButtonDefinition = () => {
	const { t } = useLocale(PageCode['moje-podani']);
	const { t: td } = useLocale(PageCode['datove-schranky']);

	return (
		connect: SubmissionConnection['connect']['connect'],
		message: DataMessage
	): FormButtonProps => ({
		color: 'primary',
		type: 'outlined',
		size: 's',
		'aria-label': t('akce.pripojit-k-jinemu-podani'),
		children: td('akce.pripojit-k-moje-podani'),
		onClick: () => connect(message),
	});
};

type Props = SubmissionConnection['connect'] & {
	message: DataMessage;
};

export const ConnectToAnotherSubmission = ({
	message,
	connect,
	...props
}: Props) => {
	const button = useConnectButtonDefinition();

	return (
		<div>
			<ConnectToAnotherSubmissionDialog {...props} />
			<Button {...button(connect, message)} />
		</div>
	);
};
